import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.esm.js';
import ZoomPlugin from 'wavesurfer.js/dist/plugins/zoom.esm.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js';
import Hover from 'wavesurfer.js/dist/plugins/hover.esm.js'
import { logout, getCurrentUser, getProject } from './api'; // getProject をインポート
import { useNavigate } from 'react-router-dom';
import './App.css';
import AudioProcessor from './AudioProcessor';
import ProjectList from './ProjectList';
import { bufferToWave, runWithAudioConstraints } from './utils';
import InputLevelMeter from './InputLevelMeter'; // InputLevelMeterのインポート
import ConfirmModal from './ConfirmModal';

function MainComponent() {
    const wavesurferRef = useRef(null);
    const [record, setRecord] = useState(null);
    const [recording, setRecording] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [thru, setThru] = useState(false);
    const [showButtons, setShowButtons] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showUploadConfirm, setShowUploadConfirm] = useState(false); // 新しい状態変数
    const [showRecordConfirm, setShowRecordConfirm] = useState(false); // 音声収録確認用
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showProjectList, setShowProjectList] = useState(false); // プロジェクトリストを表示するための状態
    const [currentProject, setCurrentProject] = useState(null); // 現在のプロジェクト
    const [audioBlob, setAudioBlob] = useState(null);
    const [ballCoordinates, setBallCoordinates] = useState([]); // ボールの座標を保持するstate
    const [indexRef, setIndexRef] = useState([]); // フレーム毎のインデックスを保持するstate
    const [isProjectLoaded, setIsProjectLoaded] = useState(false); // プロジェクトがロードされたかどうかのフラグ
    const [project, setProject] = useState(null);
    const [audioContext, setAudioContext] = useState(null);
    const audioContextRef = useRef(null);
    const audioContextInputMeterRef = useRef(null);
    // const [mediaSourceNode, setSourceNode] = useState(null);
    const mediaSourceNodeRef = useRef(null);
    // const [gainNode, setGainNode] = useState(null);
    const gainNodeRef = useRef(null);
    const filterRef = useRef(null);
    // const scriptNodeRef = useRef(null);
    const workletNodeRef = useRef(null);
    const compressorRef = useRef(null);

    const [isThruMode, setIsThruMode] = useState(true); // スルー出力モードを管理する状態変数
    const [countdown, setCountdown] = useState(0);
    const [isCountingDown, setIsCountingDown] = useState(false);
    const [countdownIntervalId, setCountdownIntervalId] = useState(null);
    const [stream, setStream] = useState(null);
    // const [deviceId, setDeviceId] = useState('');
    const deviceIdRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);

    const navigate = useNavigate();
    const audio = new Audio();
    audio.controls = true;
    audio.crossOrigin = "anonymous";

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await getCurrentUser();
                setUser(response.data);
            } catch (error) {
                navigate('/');  // ユーザー情報の取得に失敗した場合、ログインページにリダイレクト
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [navigate]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = ''; // Chromeではこれが必要です
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        const handlePopState = (event) => {
            window.history.pushState(null, null, document.URL);
        };
        window.addEventListener('popstate', handlePopState);

        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 48000 });

        createWaveSurfer();
        if (!audioContextInputMeterRef.current) {
            audioContextInputMeterRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 48000 });
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
            if (wavesurferRef.current) {
                wavesurferRef.current.destroy();
                // wavesurferRef.current.empty();
            }
        };
    }, []);

    const createWaveSurfer = () => {
        if (wavesurferRef.current) {
            wavesurferRef.current.destroy();
            // wavesurferRef.current.empty();
        }

        const now = new Date();
        const formattedTime = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
        setCurrentProject({
            name: `新規プロジェクト-${formattedTime}`,
        });

        const wavesurfer = WaveSurfer.create({
            container: '#waveform',
            waveColor: 'rgb(200, 0, 200)',
            progressColor: 'rgb(100, 0, 100)',
            cursorColor: '#F62C2B',
            cursorWidth: 2,
            dragToSeek: true,
            plugins: [TimelinePlugin.create(),
            Hover.create({
                lineColor: '#F62C2B',
                lineWidth: 2,
                labelBackground: '#555',
                labelColor: '#fff',
                labelSize: '11px',
            }),
            ],
            media: audio,
        });
        console.log('create', wavesurfer)

        // Wavesurferのスクロールイベントを監視して履歴を操作
        wavesurfer.on('scroll', () => {
            window.history.pushState(null, null, document.URL);
        });

        // Rewind to the beginning on finished playing
        wavesurfer.on('finish', () => {
            wavesurfer.setTime(0)
            handleStop();
        })

        wavesurfer.registerPlugin(
            ZoomPlugin.create({
                // the amount of zoom per wheel step, e.g. 0.5 means a 50% magnification per scroll
                scale: 0.5,
                // Optionally, specify the maximum pixels-per-second factor while zooming
                maxZoom: 1000,
            }),
        )

        const recordPlugin = RecordPlugin.create({ scrollingWaveform: true, renderRecordedAudio: true });
        wavesurfer.registerPlugin(recordPlugin);

        recordPlugin.on('record-progress', (time) => {
            updateProgress(time);
        });

        recordPlugin.on('record-end', (blob) => {
            setRecording(false);
            setShowButtons(true); // Show buttons when recording stops
            setAudioBlob(blob);
        });

        // setWaveSurfer(wavesurfer);
        wavesurferRef.current = wavesurfer;
        setRecord(recordPlugin);

        RecordPlugin.getAvailableAudioDevices().then((devices) => {
            const micSelect = document.querySelector('#mic-select');
            micSelect.innerHTML = '';
            devices.forEach((device) => {
                const option = document.createElement('option');
                option.value = device.deviceId;
                option.text = device.label || device.deviceId;
                micSelect.appendChild(option);
            });
            // 新規録音などで一覧が再生成されても、以前選択したデバイスを維持する。
            // （復元しないと先頭=default デバイスにリセットされ、選択が無視される）
            if (deviceIdRef.current && devices.some((d) => d.deviceId === deviceIdRef.current)) {
                micSelect.value = deviceIdRef.current;
            } else {
                deviceIdRef.current = micSelect.value; // 先頭を既定選択として保持
            }
        });
    };

    const handleDestroy = async () => {
        if (wavesurferRef.current) {
            // waveSurfer.destroy();
            // setWaveSurfer(null);
            setRecord(null);
            setRecording(false);
            setPlaying(false);
            setThru(false);
            setShowButtons(false);
            // setCurrentProject(null);
            setBallCoordinates([]);
            setIndexRef([]);
            createWaveSurfer(); // WaveSurferインスタンスを再作成して最初の状態に戻す
            console.log('yahoooooooooo')
        }
    };

    const handleDestroyConfirm = () => {
        setShowConfirm(true);
    };

    const handleConfirm = () => {
        setShowConfirm(false);
        handleDestroy();
    };

    const handleCancel = () => {
        setShowConfirm(false);
    };

    // 再生モードを切り替える関数
    const switchToThruMode = (gainNode, biquadFilter, scrNode, compressor) => {
        if (mediaSourceNodeRef.current && audioContextRef.current) {
            console.log('Thru mode')
            // Bypass the filters and processors
            mediaSourceNodeRef.current.disconnect();
            gainNode.disconnect();
            scrNode.disconnect();
            biquadFilter.disconnect();
            compressor.disconnect();

            mediaSourceNodeRef.current.connect(gainNode);
            gainNode.connect(audioContextRef.current.destination);
            setIsThruMode(true);
        }
    };

    const switchToFilterMode = (gainNode, biquadFilter, scrNode, compressor) => {
        if (mediaSourceNodeRef.current && audioContextRef.current) {
            console.log('Filter mode')
            // Connect through the filters and processors
            mediaSourceNodeRef.current.disconnect();
            gainNode.disconnect();
            scrNode.disconnect();
            biquadFilter.disconnect();
            compressor.disconnect();

            mediaSourceNodeRef.current.connect(scrNode);
            scrNode.connect(gainNode);
            gainNode.connect(biquadFilter);
            biquadFilter.connect(compressor);
            compressor.connect(audioContextRef.current.destination);
            setIsThruMode(false);
        }
    };

    const updateProgress = (time) => {
        const progressElement = document.querySelector('#progress');
        const formattedTime = [
            Math.floor((time % 3600000) / 60000),
            Math.floor((time % 60000) / 1000),
        ]
            .map((v) => (v < 10 ? '0' + v : v))
            .join(':');
        progressElement.textContent = formattedTime;
    };

    const handleUpload = async (event) => {
        if (audioContextRef.current) {
            audioContextRef.current.resume();
        }

        const file = event.target.files[0];
        if (file && wavesurferRef.current) {
            setAudioBlob(file);
            setShowButtons(true); // Show buttons when the waveform is ready

            //
            await waitForAudioContext();
            const arrayBuffer = await file.arrayBuffer();
            const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);

            // モノラルに変換
            const monoAudioBuffer = audioContextRef.current.createBuffer(
                1,
                audioBuffer.length,
                audioBuffer.sampleRate
            );
            console.log('fs:', audioBuffer.sampleRate)
            monoAudioBuffer.copyToChannel(audioBuffer.getChannelData(0), 0, 0);

            // バッファを WAV ファイルに変換
            const wavBlob = bufferToWave(monoAudioBuffer);
            const objectURL = URL.createObjectURL(wavBlob);
            //

            // const objectURL = URL.createObjectURL(file);
            audio.src = objectURL;
            wavesurferRef.current.load(objectURL);
        }
    };

    const waitForAudioContext = () => {
        return new Promise((resolve) => {
            const checkAudioContext = () => {
                if (audioContextRef.current) {
                    console.log('OK', audioContextRef.current)
                    resolve();
                } else {
                    console.log('NG', audioContextRef.current)
                    setTimeout(checkAudioContext, 100);
                }
            };
            checkAudioContext();
        });
    };

    const handleUploadConfirm = () => {
        setShowUploadConfirm(true);
    };

    const handleConfirmUpload = async () => {
        await handleDestroy();
        document.getElementById('hidden-file-input').click(); // ファイル入力をトリガー
        setShowUploadConfirm(false);
    };

    const handleRecordConfirm = () => {
        setShowRecordConfirm(true); // 音声収録確認モーダルを表示
    };

    const handleConfirmRecord = async () => {
        await handleDestroy();
        // handleRecord();
        document.getElementById('record-hidden-button').click(); // 録音開始をトリガー
        setShowRecordConfirm(false);
    };

    const handleRecord = () => {
        if (audioContextRef.current) {
            audioContextRef.current.resume();
        }
        if (audioContextInputMeterRef.current) {
            audioContextInputMeterRef.current.resume();
        }

        if (record.isRecording() || record.isPaused()) {
            record.stopRecording();
            setRecording(false);
            setCountdown(0);
            setIsCountingDown(false);
            stopMeter();
            return;
        }

        if (isCountingDown) {
            // カウントダウン中にボタンが押されたらカウントダウンを停止
            clearInterval(countdownIntervalId);
            setIsCountingDown(false);
            setCountdown(0);
            return;
        }

        const micSelect = document.querySelector('#mic-select');
        deviceIdRef.current = micSelect.value;
        // setDeviceId(micSelect.value);

        // カウントダウンを開始
        setIsCountingDown(true);
        setCountdown(3);

        const intervalId = setInterval(() => {
            setCountdown(prevCountdown => {
                if (prevCountdown <= 1) {
                    clearInterval(intervalId);
                    // getUserMedia 取得時に AGC/エコーを無効化（ノイズ抑制は有効のまま）。
                    // 稼働中トラックの applyConstraints では AGC が確実に切れないため取得時に注入する。
                    runWithAudioConstraints(
                        { echoCancellation: false, noiseSuppression: true, autoGainControl: false },
                        () => record.startRecording({ deviceId: deviceIdRef.current })
                    ).then(() => {
                        setRecording(true);
                        setIsCountingDown(false);
                        startMeter();
                    }).catch((e) => {
                        console.error('録音開始に失敗:', e);
                        setRecording(false);
                        setIsCountingDown(false);
                        setCountdown(0);
                    });
                }
                return prevCountdown - 1;
            });
        }, 1000);

        setCountdownIntervalId(intervalId);
    };

    const startMeter = async () => {
        const audioContext = audioContextInputMeterRef.current;
        await audioContext.resume(); // ユーザーの操作で AudioContext を再開

        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                // 録音と同じ選択デバイスを exact 指定で使う（メーターと入力元を一致させる）。
                deviceId: deviceIdRef.current ? { exact: deviceIdRef.current } : undefined,
                // AGC/エコーは無効化（録音音量の自動変動を防ぐ）。ノイズ抑制はオン。
                echoCancellation: false,
                noiseSuppression: true,
                autoGainControl: false
            }
        });
        setStream(stream);
        // setIsRecording(true);
    };

    const stopMeter = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        // setIsRecording(false);
    };

    const handlePause = () => {
        if (record.isPaused()) {
            record.resumeRecording();
            return;
        }

        record.pauseRecording();
    };

    const handlePlayPause = () => {
        if (wavesurferRef.current) {
            wavesurferRef.current.playPause();
            setPlaying(!playing);
            setThru(false);
        }
    };

    const handleThruPlayPause = () => {
        switchToThruMode(gainNodeRef.current, filterRef.current, workletNodeRef.current, compressorRef.current);
        if (wavesurferRef.current) {
            wavesurferRef.current.playPause();
            setPlaying(!playing);
            if (playing) {
                setThru(false);
            } else {
                setThru(true);
            }
        }
    };

    const handleStop = () => {
        if (wavesurferRef.current) {
            wavesurferRef.current.stop();
            setPlaying(false);
            setThru(false);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Error logging out', error);
        }
    };

    const toggleProjectList = () => {
        if (!thru) {
            setShowProjectList(!showProjectList);
        }
    };

    const handleLoadProject = async (projectId) => {
        try {
            await handleDestroy();
            const response = await getProject(projectId);
            const project = response.data;

            // 必要に応じてプロジェクトデータをWaveSurferや他のコンポーネントにロードする
            const { audio_data, position_ref, index_ref } = project;
            // ファイルパスを直接WaveSurferにロード
            const useS3 = process.env.REACT_APP_USE_S3 === 'true'; // 環境変数からbool値を読み込む
            const audioURL = useS3 ? audio_data : `http://127.0.0.1:5001/${audio_data}`;

            audio.src = audioURL;

            // WaveSurferインスタンスが正しく初期化されているか確認
            if (!wavesurferRef.current) {
                console.error('WaveSurfer instance is not initialized');
                return;
            }
            if (audioContextRef.current) {
                audioContextRef.current.resume();
            }

            const blobResponse = await fetch(audioURL);
            const blob = await blobResponse.blob();
            const objectURL = URL.createObjectURL(blob);
            setAudioBlob(blob);

            wavesurferRef.current.load(objectURL);

            wavesurferRef.current.once('ready', () => {
                console.log('Audio loaded and ready.');
            });

            // position_refとindex_refをパースして設定
            const parsedPositionRef = JSON.parse(position_ref);
            const parsedIndexRef = JSON.parse(index_ref);
            console.log('Setting ballCoordinates and indexRef:', parsedPositionRef, parsedIndexRef);
            setBallCoordinates(parsedPositionRef);
            setIndexRef(parsedIndexRef);
            setCurrentProject(project);
            setIsProjectLoaded(true);
            setShowButtons(true);

            console.log('ballcoordinates:', ballCoordinates);

            wavesurferRef.current.once('error', (error) => {
                console.error('Error loading audio:', error);
            });

        } catch (error) {
            console.error('Error loading project:', error);
        }
        toggleProjectList();
    };

    return (
        <>
            <Helmet>
                <link
                    rel="stylesheet"
                    href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css"
                />
            </Helmet>
            <div className="main-container">
                <aside className="sidebar">
                    <div className="user-info">
                        <i className="fas fa-user-circle user-icon"></i>
                        <span className="username">{user ? user.username : 'ユーザー名'}さん</span>
                    </div>
                    <div className="logo">
                        <img src="logo_imavorec.png" alt="Logo" />
                    </div>
                    <hr className="logo-divider" />
                    <ul className="menu">
                        <li><a
                            href="#"
                            onClick={thru || isEditing || isPreviewing ? (e) => e.preventDefault() : toggleProjectList}
                            className={thru || isEditing || isPreviewing ? "disabled-link" : ""}
                        >
                            <i className="fas fa-file-audio"></i> プロジェクト一覧
                        </a></li>
                        <li><a href="https://imavo.notion.site/imavo-rec-faq" target="_blank" rel="noopener noreferrer"><i className="fas fa-question-circle"></i> ヘルプ</a></li>
                        <li><a href="https://virtual-dummy-head.notion.site/13102bc87cb780d09ecbe60a6decfef7?pvs=105" target="_blank" rel="noopener noreferrer"><i className="fas fa-envelope"></i> お問い合わせ</a></li>
                        <li><a href="#" onClick={handleLogout}><i className="fas fa-sign-out-alt"></i> ログアウト</a></li>
                    </ul>
                </aside>
                <div className="content">
                    <div className="header">
                        {currentProject?.name || "新規プロジェクト"}
                    </div>
                    <main className="main-content">
                        <div className="controls-wrapper">
                            {showButtons && (
                                <div className="left-buttons">
                                    <button onClick={handleThruPlayPause} className="icon-button play-pause" disabled={isEditing || isPreviewing}>
                                        <i className={playing ? 'fas fa-pause' : 'fas fa-play'}></i>
                                    </button>
                                    <button onClick={handleStop} className="icon-button stop" disabled={isEditing || isPreviewing}>
                                        <i className="fas fa-stop"></i>
                                    </button>
                                    <button
                                        onClick={handleRecordConfirm}
                                        className={`button upload-footer ${isCountingDown || recording ? 'disabled' : ''}`}
                                        disabled={isCountingDown || recording}
                                    >
                                        新規録音
                                    </button>
                                    <button
                                        onClick={handleUploadConfirm}
                                        className={`button upload-footer ${isCountingDown || recording ? 'disabled' : ''}`}
                                        disabled={isCountingDown || recording}
                                    >
                                        新規アップロード
                                    </button>
                                </div>
                            )}
                            <button id="record-hidden-button" onClick={handleRecord} className="hidden"></button>
                            <input type="file" accept="audio/*" onChange={handleUpload} className="hidden" id="hidden-file-input" />
                            <div id="save-buttons"></div>
                        </div>
                        <div className="waveform-container">
                            <div id="waveform" className="waveform"></div>
                            <div id="recordings" className="recordings"></div>
                            {isCountingDown && (
                                <div className="countdown-overlay">
                                    <span className="countdown-number">{countdown}</span>
                                </div>
                            )}
                        </div>
                        <div>
                            {!showButtons && (
                                <>
                                    <div className="flex-container">
                                        <button onClick={handleRecord} className={`button record ${recording ? 'recording blinking' : ''}`}>
                                            {recording ? '録音停止' : (isCountingDown ? '録音停止' : '録音開始')}
                                        </button>
                                        <input type="file" accept="audio/*" onChange={handleUpload} className="hidden" id="upload-input" disabled={isCountingDown || recording} />
                                        <label htmlFor="upload-input" className={`button upload ${isCountingDown || recording ? 'disabled' : ''}`}>
                                            アップロード
                                        </label>
                                    </div>
                                </>
                            )}
                        </div>
                        <div id="progress" className="progress"></div>
                        <div>
                            {showButtons && user && (
                                <>
                                    {wavesurferRef.current && <AudioProcessor
                                        user={user}
                                        waveSurfer={wavesurferRef.current}
                                        switchToFilterMode={switchToFilterMode}
                                        audioContextRef={audioContextRef}
                                        mediaSourceNodeRef={mediaSourceNodeRef}
                                        gainNodeRef={gainNodeRef}
                                        filterRef={filterRef}
                                        workletNodeRef={workletNodeRef}                                        compressorRef={compressorRef}
                                        blob={audioBlob}
                                        thru={thru}
                                        ballCoordinates={ballCoordinates}
                                        indexRef={indexRef}
                                        currentProject={currentProject}
                                        setCurrentProject={setCurrentProject}
                                        isProjectLoaded={isProjectLoaded}
                                        setIsProjectLoaded={setIsProjectLoaded}
                                        handleDestroyConfirm={handleDestroyConfirm}
                                        isEditing={isEditing}
                                        setIsEditing={setIsEditing}
                                        isPreviewing={isPreviewing}
                                        setIsPreviewing={setIsPreviewing}
                                    />}
                                </>
                            )}
                        </div>
                        {showConfirm && (
                            <ConfirmModal
                                message="現在のプロジェクトで保存していないものは破棄されます。本当によろしいですか？"
                                onConfirm={handleConfirm}
                                onCancel={handleCancel}
                            />
                        )}
                        {showUploadConfirm && (
                            <ConfirmModal
                                message="現在のプロジェクトで保存していないものは破棄されます。本当によろしいですか？"
                                onConfirm={handleConfirmUpload}
                                onCancel={() => setShowUploadConfirm(false)}
                            />
                        )}
                        {showRecordConfirm && (
                            <ConfirmModal
                                message="現在のプロジェクトで保存していないものは破棄されます。本当によろしいですか？"
                                onConfirm={handleConfirmRecord}
                                onCancel={() => setShowRecordConfirm(false)}
                            />
                        )}
                    </main>
                    {showProjectList && (
                        <div className="overlay" onClick={toggleProjectList}>
                            <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
                                <button className="close-icon-btn" onClick={toggleProjectList}>
                                    <i className="fas fa-times"></i>
                                </button>
                                <ProjectList
                                    onProjectSelect={handleLoadProject}
                                    onProjectDelete={handleDestroy}
                                    currentProjectId={currentProject ? currentProject.id : null} />
                            </div>
                        </div>
                    )}

                    <footer className="footer">
                        <div className="left-controls"></div>
                        <div className="right-controls">
                            <div className="label-meter-group">
                                <span className="label-mic-setting">🎙️マイク設定</span>
                                <select id="mic-select" className="mic-select" disabled={isCountingDown || recording} onChange={(e) => { deviceIdRef.current = e.target.value; }}></select>
                                <span className="label-meter">入力レベル</span>
                                <InputLevelMeter stream={stream} audioContext={audioContextInputMeterRef.current} />
                            </div>
                        </div>
                    </footer>
                </div>
            </div>
        </>

    );
}

export default MainComponent;
